import { Component, OnDestroy } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { ExperienceListingRow } from './experience-listing-row.model';
import {
	GetExperienceListingsRequest,
	ListingStatus,
	UpdateExperienceListingStatusRequest,
} from 'app/experience/models';
import { MenuPermissionActionEnum, MenuPermissionConfig } from 'app/models';
import { AppDataService } from 'app/services/app.data.service';
import { ListingReviewService } from 'app/experience/pages/listing-review/listing-review.service';

@Component({
	selector: 'experience-listings',
	providers: [ListingReviewService, MessageService],
	templateUrl: './experience-listings.component.html',
	styleUrl: './experience-listings.component.scss',
})
export class ExperienceListingsComponent implements OnDestroy {
	readonly ListingStatus = ListingStatus;

	/** Mirrors backend ExperienceListingService.AllowedTransitions */
	private static readonly statusTransitions: Readonly<Record<ListingStatus, ListingStatus[]>> = {
		[ListingStatus.None]: [],
		[ListingStatus.Draft]: [ListingStatus.Approved, ListingStatus.Inactive, ListingStatus.Rejected],
		[ListingStatus.Inactive]: [ListingStatus.Draft, ListingStatus.Active],
		[ListingStatus.Approved]: [ListingStatus.Active, ListingStatus.Draft, ListingStatus.Inactive],
		[ListingStatus.Active]: [ListingStatus.Inactive],
		[ListingStatus.Rejected]: [ListingStatus.Draft, ListingStatus.InReview],
		[ListingStatus.InReview]: [ListingStatus.Approved, ListingStatus.Rejected],
	};

	private static readonly statusLabels: Record<ListingStatus, string> = {
		[ListingStatus.None]: 'None',
		[ListingStatus.Draft]: 'Draft',
		[ListingStatus.Active]: 'Active',
		[ListingStatus.Inactive]: 'Inactive',
		[ListingStatus.Approved]: 'Approved',
		[ListingStatus.Rejected]: 'Rejected',
		[ListingStatus.InReview]: 'In Review',
	};

	/** One frozen options array per current status (shared across rows with same status). */
	private static readonly actionStatusOptionsByStatus: ReadonlyMap<
		ListingStatus,
		ReadonlyArray<{ label: string; value: ListingStatus }>
	> = ExperienceListingsComponent.buildActionStatusOptionsByStatus();

	private static buildActionStatusOptionsByStatus(): ReadonlyMap<
		ListingStatus,
		ReadonlyArray<{ label: string; value: ListingStatus }>
	> {
		const map = new Map<ListingStatus, ReadonlyArray<{ label: string; value: ListingStatus }>>();
		const allStatuses: ListingStatus[] = [
			ListingStatus.None,
			ListingStatus.Draft,
			ListingStatus.Active,
			ListingStatus.Inactive,
			ListingStatus.Approved,
			ListingStatus.Rejected,
			ListingStatus.InReview,
		];
		for (const status of allStatuses) {
			const allowed = ExperienceListingsComponent.statusTransitions[status] ?? [];
			const values = new Set<ListingStatus>([status, ...allowed]);
			const arr = Object.freeze(
				Array.from(values)
					.sort((a, b) => a - b)
					.map(value => ({
						label: ExperienceListingsComponent.statusLabels[value],
						value,
					})),
			);
			map.set(status, arr);
		}
		return map;
	}

	private static actionStatusOptionsForStatus(
		status: ListingStatus,
	): ReadonlyArray<{ label: string; value: ListingStatus }> {
		return (
			ExperienceListingsComponent.actionStatusOptionsByStatus.get(status) ??
			ExperienceListingsComponent.actionStatusOptionsByStatus.get(ListingStatus.None) ??
			[]
		);
	}

	readonly statusFilterOptions: { label: string; value: ListingStatus }[] = [
		{ label: 'Draft', value: ListingStatus.Draft },
		{ label: 'Inactive', value: ListingStatus.Inactive },
		{ label: 'Active', value: ListingStatus.Active },
		{ label: 'Approved', value: ListingStatus.Approved },
		{ label: 'Rejected', value: ListingStatus.Rejected },
		{ label: 'In Review', value: ListingStatus.InReview },
	];

	/** Column layout: width + optional sort field for header row. */
	readonly columns: ReadonlyArray<{
		width: string;
		sortField: string | null;
		label: string;
	}> = [
		{ width: '100px', sortField: 'Id', label: 'Id' },
		{ width: '200px', sortField: 'Title', label: 'Title' },
		{ width: '160px', sortField: 'TourOperatorName', label: 'Operator' },
		{ width: '160px', sortField: 'LocationName', label: 'Location' },
		{ width: '160px', sortField: 'CategoryName', label: 'Category' },
		{ width: '140px', sortField: 'BasePrice', label: 'Base price' },
		{ width: '140px', sortField: 'StatusName', label: 'Status' },
		{ width: '180px', sortField: null, label: 'Action' },
	];

	query = { skip: 0, take: 25, sortField: 'Id', sortOrder: -1 };

	filter: {
		id: number | null;
		title: string;
		tourOperatorName: string;
		locationName: string;
		categoryName: string;
		basePrice: number | null;
		status: ListingStatus | null;
	} = {
		id: null,
		title: '',
		tourOperatorName: '',
		locationName: '',
		categoryName: '',
		basePrice: null,
		status: null,
	};

	listings: ExperienceListingRow[] = [];
	totalRecords = 0;
	loading = false;
	rowsPerPageOptions: number[] = [12, 25, 50, 100];
	searchTimer = 0;

	/**
	 * Incremented on each `loadListings` call; stale responses are ignored in `.then` / `.finally`
	 * (same pattern as `ListingReviewPageComponent.latestRequestId`).
	 */
	private latestListingsRequestId = 0;

	/** Row id while status API is in flight; disables that row’s dropdown. */
	updatingListingId: number | null = null;

	get canUpdateListingStatus(): boolean {
		const listingPermision = MenuPermissionConfig.EXPERIENCE_LISTING_REVIEW;
		return (
			this.appData.hasPermission(listingPermision, MenuPermissionActionEnum.Update) ||
			this.appData.hasPermission(listingPermision, MenuPermissionActionEnum.Create)
		);
	}

	constructor(
		private readonly listingReviewService: ListingReviewService,
		private readonly messageService: MessageService,
		private readonly appData: AppDataService,
	) {}

	ngOnDestroy(): void {
		if (this.searchTimer) {
			window.clearTimeout(this.searchTimer);
			this.searchTimer = 0;
		}
	}

	handleLazyLoad(event: TableLazyLoadEvent): void {
		if (this.searchTimer) {
			window.clearTimeout(this.searchTimer);
			this.searchTimer = 0;
		}

		this.query.skip = event.first ?? 0;
		this.query.take = event.rows ?? this.query.take;
		if (event.sortField !== undefined && event.sortField !== null) {
			const sf = event.sortField;
			this.query.sortField = Array.isArray(sf) ? String(sf[0]) : String(sf);
		}
		if (event.sortOrder !== undefined && event.sortOrder !== null) {
			this.query.sortOrder = event.sortOrder;
		}
		Promise.resolve().then(() => this.loadListings());
	}

	handleFilterChange(): void {
		if (this.searchTimer) {
			window.clearTimeout(this.searchTimer);
		}
		this.searchTimer = window.setTimeout(() => {
			this.query.skip = 0;
			this.loadListings();
			this.searchTimer = 0;
		}, 1500);
	}

	private buildListingsRequest(): GetExperienceListingsRequest {
		const req: GetExperienceListingsRequest = {
			skip: this.query.skip,
			take: this.query.take,
			sortField: this.query.sortField,
			sortOrder: this.query.sortOrder,
		};

		if (this.filter.id != null && this.filter.id > 0) {
			req.listingId = this.filter.id;
		}

		const title = this.filter.title.trim();
		if (title) {
			req.title = title;
		}

		const tourOp = this.filter.tourOperatorName.trim();
		if (tourOp) {
			req.tourOperatorName = tourOp;
		}

		const loc = this.filter.locationName.trim();
		if (loc) {
			req.locationName = loc;
		}

		const cat = this.filter.categoryName.trim();
		if (cat) {
			req.categoryName = cat;
		}

		if (this.filter.basePrice != null && !Number.isNaN(this.filter.basePrice)) {
			req.basePrice = this.filter.basePrice;
		}

		if (this.filter.status != null) {
			req.statuses = [this.filter.status];
		}

		return req;
	}

	private mapListingDto(dto: any): ExperienceListingRow {
		const status = dto.Status as ListingStatus;
		return {
			id: dto.Id,
			tourOperatorId: dto.TourOperatorId ?? dto.tourOperatorId ?? 0,
			title: dto.Title,
			locationName: dto.LocationName ?? dto.locationName ?? '',
			categoryName: dto.CategoryName ?? dto.categoryName ?? '',
			tourOperatorName: dto.TourOperatorName ?? dto.tourOperatorName ?? '',
			basePrice: dto.BasePrice ?? 0,
			status,
			statusName: dto.StatusName ?? dto.statusName ?? '',
			actionStatusOptions: ExperienceListingsComponent.actionStatusOptionsForStatus(status),
		};
	}

	onStatusChange(row: ExperienceListingRow, newStatus: ListingStatus): void {
		if (!this.canUpdateListingStatus) {
			return;
		}
		if (newStatus === row.status || this.updatingListingId !== null) {
			return;
		}
		if (!row.id || row.tourOperatorId <= 0) {
			this.messageService.add({
				severity: 'error',
				summary: 'Error',
				detail: 'Missing listing or tour operator.',
			});
			return;
		}

		this.updatingListingId = row.id;
		const request: UpdateExperienceListingStatusRequest = {
			listingId: row.id,
			tourOperatorId: row.tourOperatorId,
			status: newStatus,
		};

		this.listingReviewService
			.updateExperienceListingStatus(request, this.messageService)
			.then(response => {
				if (response == null) {
					return;
				}
				if (!response.Success) {
					this.messageService.add({
						severity: 'error',
						summary: 'Error',
						detail: response.ErrorMessage ?? 'Could not update listing status.',
					});
					this.loadListings();
					return;
				}
				this.messageService.add({
					severity: 'success',
					summary: 'Success',
					detail: 'Status updated.',
				});
				this.loadListings();
			})
			.finally(() => {
				this.updatingListingId = null;
			});
	}

	loadListings(): void {
		const requestId = ++this.latestListingsRequestId;
		this.loading = true;
		const request = this.buildListingsRequest();

		this.listingReviewService
			.getExperienceListings(request, this.messageService)
			.then(data => {
				if (requestId !== this.latestListingsRequestId) {
					return;
				}

				if (data == null) {
					return;
				}

				if (!data.Success) {
					this.messageService.add({
						severity: 'error',
						summary: 'Error',
						detail: data.ErrorMessage ?? 'Failed to load listings',
					});
					return;
				}

				this.totalRecords = data.TotalRecords ?? 0;
				this.listings = (data.Listings ?? []).map((dto: any) => this.mapListingDto(dto));
			})
			.finally(() => {
				if (requestId !== this.latestListingsRequestId) {
					return;
				}
				this.loading = false;
			});
	}
}
